import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import AdminSidebar from "@/components/layout/AdminSidebar";
import Brand from "@/components/common/Brand";
import { cn } from "@/lib/utils";
import { ADMIN } from "@/constants/testIds";
import {
  getAdminSession,
  logoutAdmin,
  ADMIN_ROLE_LABELS,
} from "@/data/adminAuthStore";
import { getActiveModuleLabel } from "@/data/adminMenu";

/**
 * Layout independiente del portal administrativo.
 * - No comparte navegación con la app del socio ni con Recepción.
 * - Optimizado para escritorio y tablet (no móvil aún, pero conserva drawer).
 * - Encabezado con: marca, pantalla activa, usuario, rol y cerrar sesión.
 */
export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState(getAdminSession());

  useEffect(() => {
    // Refresca la sesión cuando cambia la ruta (por ejemplo, tras login).
    setSession(getAdminSession());
  }, [location.pathname]);

  const screenName = getActiveModuleLabel(location.pathname);
  const nombre = session?.nombre || session?.usuario || "";
  const rolLabel = session?.rol ? ADMIN_ROLE_LABELS[session.rol] || session.rol : "";
  const iniciales = nombre
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = () => {
    logoutAdmin();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-muted/40 text-foreground">
      {/* Sidebar (escritorio/tablet) */}
      <aside
        data-testid={ADMIN.sidebar}
        className="hidden md:flex fixed inset-y-0 left-0 w-72 flex-col bg-card border-r hairline z-40"
      >
        <div className="px-8 py-6 border-b hairline">
          <Brand />
        </div>
        <AdminSidebar />
        <div className="px-8 py-6 border-t hairline">
          <p className="overline text-muted-foreground">Portal privado</p>
          <p className="text-xs text-muted-foreground mt-1">
            Casino Tampiqueño · Uso interno
          </p>
        </div>
      </aside>

      {/* Drawer móvil (soporte básico, no es prioridad este sprint) */}
      <div
        className={cn(
          "md:hidden fixed inset-0 z-50 transition-opacity duration-300",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        )}
      >
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <aside
          className={cn(
            "absolute inset-y-0 left-0 w-72 bg-card border-r hairline flex flex-col",
            "transition-transform duration-300 ease-out",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="px-6 py-5 border-b hairline flex items-center justify-between">
            <Brand />
            <button
              type="button"
              aria-label="Cerrar menú"
              onClick={() => setOpen(false)}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <AdminSidebar onNavigate={() => setOpen(false)} testIdSuffix="-mobile" />
        </aside>
      </div>

      {/* Contenido principal */}
      <div className="md:pl-72 flex flex-col min-h-[100dvh]">
        <header
          data-testid={ADMIN.topbar}
          className="sticky top-0 z-30 glass border-b hairline"
        >
          <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            {/* Izquierda: pantalla activa (desktop) / hamburger (mobile) */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                data-testid={ADMIN.mobileToggle}
                aria-label="Abrir menú"
                onClick={() => setOpen(true)}
                className="md:hidden w-10 h-10 grid place-items-center rounded-xl border hairline bg-card hover:bg-muted transition-colors shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden md:flex flex-col min-w-0">
                <span className="overline text-muted-foreground">
                  CT Connect · Consola
                </span>
                <span
                  data-testid={ADMIN.headerScreenName}
                  className="font-heading text-lg truncate"
                >
                  {screenName}
                </span>
              </div>
              <div className="md:hidden flex items-center gap-2 min-w-0">
                <Brand variant="mark" />
                <span
                  data-testid={`${ADMIN.headerScreenName}-mobile`}
                  className="font-heading text-base truncate"
                >
                  {screenName}
                </span>
              </div>
            </div>

            {/* Derecha: usuario, rol y cerrar sesión */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col text-right leading-tight min-w-0">
                <span
                  data-testid={ADMIN.headerUserName}
                  className="text-sm font-medium truncate max-w-[14rem]"
                >
                  {nombre}
                </span>
                <span
                  data-testid={ADMIN.headerUserRole}
                  className="overline text-accent"
                >
                  {rolLabel}
                </span>
              </div>
              <div
                aria-hidden="true"
                className="w-10 h-10 rounded-full bg-accent/20 ring-1 ring-accent/40 grid place-items-center"
              >
                <span className="font-heading text-sm text-accent-foreground">
                  {iniciales || "CT"}
                </span>
              </div>
              <button
                type="button"
                data-testid={ADMIN.btnLogout}
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span className="hidden sm:inline">Cerrar sesión</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 md:px-10 py-8 md:py-12 max-w-6xl w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
